import {
  analyzeCategory,
  hasCategoryEvidence,
  isSummaryText as isSharedSummaryText,
} from '../../../utils/categoryClassifier';
import {
  CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN,
  CARE_MEDICATION_SCHOOL_DESCRIPTION_PATTERN,
  CARE_MEDICATION_SCHOOL_NAME_PATTERN,
} from '../../../shared/careMedicationPatterns';
import { CARE_BEHAVIOR_REFUSAL_PATTERN } from '../../../shared/careSignalPatterns';
import { buildNaturalCaptureCategoryDecision } from './naturalCaptureCategoryCore';
import { normalizeTextForImport } from './importLogsParsingText';

export const normalizeImportCategory = (category) => {
  const normalized = String(category || 'log').trim().toLowerCase();
  const aliases = {
    daily: 'log',
    other: 'log',
    note: 'log',
    log: 'log',
    dailylog: 'log',
    behavior: 'behavior',
    behavior_meltdown: 'behavior',
    behavioral: 'behavior',
    meltdown: 'behavior',
    mood: 'mood',
    anxiety: 'mood',
    health: 'health',
    milestone: 'milestone',
    sleep: 'sleep',
    food: 'food',
    activity: 'activity',
    medication: 'medication',
    mealtime: 'food',
    bathroom: 'bathroom',
    toilet: 'bathroom',
    toileting: 'bathroom',
    medical_note: 'health',
  };

  return aliases[normalized] || 'log';
};

const SCHOOL_FILLER_PATTERNS = [
  /^(hi|hello|dear|good morning|good afternoon|good evening)\b/i,
  /^(best|regards|sincerely|warmly|cheers|thanks|thank you)\b/i,
  /^(ms|mrs|mr)\.?\s+[a-z]+$/i,
  /^(from|sent|to|cc|bcc|subject|date)\s*:/i,
  /^i wanted to share/i,
  /^overall[,.]/i,
  /^please let us know if you have any questions/i,
  /^if you have any questions/i,
  /^have a great day/i,
  /^let me know if you have any questions/i,
];

export const hasStrongSchoolStructureForCategory = (category = '', text = '') => hasCategoryEvidence(normalizeImportCategory(category), text);

const isSchoolFillerText = (text = '') => {
  const normalized = String(text || '').trim();
  if (!normalized) return true;
  const comparable = normalizeTextForImport(normalized);
  if (!comparable) return true;
  if (SCHOOL_FILLER_PATTERNS.some((pattern) => pattern.test(normalized))) return true;
  return comparable.length <= 4;
};

const hasBehaviorIncidentSignal = (text = '') => hasCategoryEvidence('behavior', text);

const BEHAVIOR_ACTION_PRIORITY_PATTERNS = [
  /\b(?:cry|cried|crying)\b/i,
  CARE_BEHAVIOR_REFUSAL_PATTERN,
  /\b(?:aggress(?:ion|ive)?|hit|hitting|kick(?:ed|ing)?|bit(?:e|ing)?|throw(?:ing|n)?)\b/i,
  /\b(?:redirect(?:ed|ion)?|needed redirection|needed reminders?|needed prompts?|needed repeated prompts?|needed prompting|needed support|one on one support|one-on-one support)\b/i,
  /\b(?:transition difficulty|difficulty with transition|upset during transition|struggled with transition)\b/i,
  /\b(?:sat out|needed a break|took a break|rejoin(?:ed|ing)?|returned to (?:group|class|activity))\b/i,
  /\b(?:distracted|off task|off-task|trouble focusing|difficulty focusing)\b/i,
  /\b(?:covered (?:his|her|their) ears|covered ears|sensory break|sensory breaks|overstimulat(?:ed|ion)|too noisy|noise(?:\s+)?was too much)\b/i,
];

const MOOD_DESCRIPTION_PATTERNS = [
  /\b(?:calm|calmer|happy|cheerful|quiet|tired|sad|content|worried|anxious|irritable|cranky|upset|frustrated|settled|regulated|improved|better|back to baseline|regrouped|able to engage again|engaged again|difficulty settling in|hard time settling in)\b/i,
];

const MEDICATION_DESCRIPTION_PATTERNS = [
  CARE_MEDICATION_SCHOOL_NAME_PATTERN,
  CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN,
  CARE_MEDICATION_SCHOOL_DESCRIPTION_PATTERN,
];

export const hasStrongBehaviorActionSignal = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;
  return BEHAVIOR_ACTION_PRIORITY_PATTERNS.some((pattern) => pattern.test(comparable))
    || hasBehaviorIncidentSignal(comparable);
};

export const hasStandaloneFoodEventSignal = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;

  return /\b(?:ate|drank|eating|drinking|lunch|snack|breakfast|dinner|meal|juice|few bites)\b/i.test(comparable);
};

const hasStrongSleepSignal = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;

  return /\b(?:sleep|slept|asleep|fall asleep|fell asleep|nap|napped|rest time|rested|lay quietly|stayed on (?:her|his|their) mat)\b/i.test(comparable);
};

export const hasMoodOnlySignal = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;
  return MOOD_DESCRIPTION_PATTERNS.some((pattern) => pattern.test(comparable))
    && !hasStrongBehaviorActionSignal(comparable);
};

export const hasStrongMedicationSignal = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;

  return MEDICATION_DESCRIPTION_PATTERNS.some((pattern) => pattern.test(comparable));
};

export const classifySchoolSentenceCategory = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return null;
  if (hasStrongBehaviorActionSignal(comparable)) return 'behavior';
  if (hasStandaloneFoodEventSignal(comparable)) return 'food';
  if (hasStrongMedicationSignal(comparable)) return 'medication';
  if (hasStrongSleepSignal(comparable)) return 'sleep';
  if (hasMoodOnlySignal(comparable)) return 'mood';

  const analysis = analyzeCategory(text, { allowUnknown: true });
  if (!analysis.category || analysis.category === 'unknown') return null;
  return analysis.category;
};

export const classifySchoolBlockCategory = (text = '') => classifySchoolSentenceCategory(text);

export const isBehaviorContinuationSentence = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;

  return /(?:\bit lasted\b|\blast(?:ed)?\b|\bafter about\b|\babout \d+\s?minutes?\b|\bcalmed down\b|\bsettled\b|\bneeded support\b|\bwith support\b|\bredirected\b|\breturned to class\b|\brejoined\b|\bsat out\b|\bthen\b|\bbefore she\b|\bbefore he\b|\bbefore they\b|\bwith prompting\b|\bwith help\b)/i.test(comparable);
};

export const looksLikeSchoolEvent = (text = '') => {
  if (hasStandaloneFoodEventSignal(text)) {
    return true;
  }

  const analysis = analyzeCategory(text, { allowUnknown: true });
  return Boolean(analysis.category && analysis.category !== 'unknown');
};

export const isSchoolIntroOrClosing = (text = '') => {
  if (isSchoolFillerText(text)) return true;
  const comparable = normalizeTextForImport(text);
  return /^hi\b|^hello\b|^dear\b/.test(comparable)
    || /^best\b|^regards\b|^sincerely\b|^thank you\b|^thanks\b/.test(comparable)
    || isSharedSummaryText(comparable)
    || comparable.includes('please let us know if you have any questions')
    || comparable.includes('i wanted to share how')
    || (comparable.includes('overall') && comparable.includes('positive day'));
};

const FOOD_CONTINUATION_PATTERNS = [
  /\b(?:the rest of (?:her|his|their) meal|rest of (?:her|his|their) meal|did not want the rest of (?:her|his|their) meal|didn't want the rest of (?:her|his|their) meal)\b/i,
  /\b(?:finished(?:\s+her|\s+his|\s+their)?(?:\s+juice|\s+snack|\s+meal)|finished(?:\s+her|\s+his|\s+their)?\s+juice|finished(?:\s+her|\s+his|\s+their)?\s+snack|finished(?:\s+her|\s+his|\s+their)?\s+meal)\b/i,
  /\b(?:continued eating|ate more|had more|more of (?:her|his|their) lunch|more of (?:her|his|their) meal|drank more|wanted more)\b/i,
  /\b(?:drank (?:her|his|their) (?:juice|water|milk))\b/i,
  /\b(?:ate (?:part of|some of|most of)(?: (?:her|his|their))?)\b/i,
  /\b(?:ate well|ate everything|ate it all)\b/i,
];

const MEDICATION_CONTINUATION_PATTERNS = [
  /\b(?:no issues|no problems|without issues|without issue|tolerated well|went well|no side effects|side effect free)\b/i,
  /\b(?:medication was given|meds were given|gave medication|administered medication|dose was given)\b/i,
];

const MOOD_CONTINUATION_PATTERNS = [/\b(?:calmer|settled|regulated|able to engage again|engaged again|back to baseline|improved|regrouped)\b/i];
const SLEEP_CONTINUATION_PATTERNS = [
  /\b(?:did not fall asleep|didn't fall asleep|did not sleep|didn't sleep|stayed quiet|rested quietly|lay quietly|slept quietly|remained on (?:her|his|their) mat|stayed on (?:her|his|their) mat)\b/i,
];

const EVENT_SHIFT_PATTERNS = [
  /^(?:later|then|afterwards|afterward|next|subsequently|soon after)\b/i,
  /\b(?:then|afterwards|afterward|next|subsequently|soon after)\b/i,
  /^(?:around|about|at|by|during|near|before|after)\b/i,
  /\b(?:later in the day|later that day|later in the afternoon|later in the morning)\b/i,
];

export const isCategoryContinuationSentence = (category = '', text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;

  switch (normalizeImportCategory(category)) {
    case 'behavior': return isBehaviorContinuationSentence(comparable);
    case 'food': return FOOD_CONTINUATION_PATTERNS.some((pattern) => pattern.test(comparable));
    case 'medication': return MEDICATION_CONTINUATION_PATTERNS.some((pattern) => pattern.test(comparable));
    case 'mood': return MOOD_CONTINUATION_PATTERNS.some((pattern) => pattern.test(comparable));
    case 'sleep': return SLEEP_CONTINUATION_PATTERNS.some((pattern) => pattern.test(comparable));
    default: return false;
  }
};

export const hasEventShiftCue = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;
  return EVENT_SHIFT_PATTERNS.some((pattern) => pattern.test(comparable));
};

const isContextOnlyMoodSentence = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return false;
  if (!/\b(?:during|in|while|at)\s+(?:group activity|class|classroom|circle time|work time|morning work|activity|center|centers)\b/i.test(comparable)) return false;
  return !/\b(?:anxious|worried|upset|frustrated|irritable|cranky|sad|distressed|dysregulated|improved|calmer|settled|regulated|able to engage|engaged again|back to baseline|regrouped|difficulty|struggled|hard time|trouble)\b/i.test(comparable);
};

export const parseSchoolEventImportance = (text = '') => {
  const comparable = normalizeTextForImport(text);
  return hasBehaviorIncidentSignal(comparable) || /(?:important|urgent|high concern|major concern|escalation|behavior concern|aggression|meltdown|tantrum|school refusal|required support|needed a break)/i.test(comparable)
    ? 'important'
    : 'normal';
};

export const getSchoolImportConfidence = ({
  cleanText = '',
  category = '',
  categorySource = 'direct',
  timeSource = 'explicit',
  chunkText = '',
}) => {
  const comparable = normalizeTextForImport(cleanText || chunkText);
  const reasons = [];
  const categoryValue = normalizeImportCategory(category);
  const categoryFallback = categorySource === 'fallback';
  const hasInferredTime = timeSource === 'approximate' || timeSource === 'inferred';
  const directCategoryMatch = Boolean(classifySchoolSentenceCategory(cleanText));
  const strongStructure = category === 'mood'
    ? hasMoodOnlySignal(cleanText)
    : hasStrongSchoolStructureForCategory(categoryValue, cleanText);
  const uncertainLanguage = /(overall|later|afternoon|summary|much better|better|improved|improving)/i.test(comparable);
  const vagueText = !directCategoryMatch && (comparable.length < 45 || comparable.split(' ').length <= 5 || uncertainLanguage);

  if (hasInferredTime) reasons.push('inferred_time');
  if (categoryFallback) reasons.push('category_fallback');
  if (vagueText) reasons.push('vague_text');
  if (!strongStructure) reasons.push('weak_structure');

  return {
    confidence: reasons.length ? 'review' : 'high',
    confidenceReasons: reasons,
    confidenceLabel: reasons.length ? 'Quick check' : 'Looks right',
  };
};

export const shouldKeepImportedSchoolRow = ({ cleanText = '', category = '', confidenceState = {}, chunk = {} }) => {
  if (!cleanText || isSchoolIntroOrClosing(cleanText)) return false;

  const normalizedCategory = normalizeImportCategory(category);
  if (normalizedCategory === 'activity') return false;
  const hasStrongStructure = category === 'mood'
    ? hasMoodOnlySignal(cleanText)
    : hasStrongSchoolStructureForCategory(normalizedCategory, cleanText);
  if (!hasStrongStructure) return false;

  if (category === 'mood') {
    if (isContextOnlyMoodSentence(cleanText)) return false;
    if (/^later\b/i.test(cleanText) && !/\b(?:able to engage again|engaged again|back to baseline|settled|regrouped|improved)\b/i.test(cleanText)) return false;
  }

  if (confidenceState?.confidence === 'review' && chunk?.shortInput) return false;
  return Boolean(normalizedCategory && normalizedCategory !== 'log');
};

export const resolveChunkCategory = (chunk = {}, cleanText = '') => {
  const chunkCategory = chunk.category && chunk.category !== 'unknown' ? chunk.category : null;
  const fallbackAnalysis = chunkCategory ? null : analyzeCategory(cleanText, { allowUnknown: true });
  const fallbackCategory = fallbackAnalysis?.category && fallbackAnalysis.category !== 'unknown' ? fallbackAnalysis.category : null;
  const categoryDecision = buildNaturalCaptureCategoryDecision(cleanText, {
    currentPathCategory: chunkCategory || fallbackCategory || null,
    previousCategory: chunk?.previousCategory || null,
    sourceMode: chunk?.sourceMode || 'school_text',
    inputMode: chunk?.inputMode || 'import',
    parserMode: chunk?.parserMode || 'school_text',
    shapeHint: chunk?.shapeHint || 'school_text',
    categoryHint: chunkCategory || fallbackCategory || null,
    confidenceMetadata: chunk?.confidenceState || null,
    categorySource: chunkCategory ? 'chunk' : (fallbackCategory ? 'classified' : 'fallback'),
  });

  return {
    chunkCategory,
    fallbackAnalysis,
    fallbackCategory,
    category: chunkCategory || fallbackCategory,
    categorySource: chunkCategory ? 'chunk' : (fallbackCategory ? 'classified' : 'fallback'),
    categoryDecision,
  };
};
